<script setup>
import { VForm } from 'vuetify/components/VForm'
import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import { themeConfig } from '@themeConfig'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/mask-v2-dark.png'
import authV2MaskLight from '@images/pages/mask-v2-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

const authThemeImg = useGenerateImageVariant(
  authV2LoginIllustrationLight,
  authV2LoginIllustrationDark,
  authV2LoginIllustrationBorderedLight,
  authV2LoginIllustrationBorderedDark,
  true
)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isPasswordVisible = ref(false)
const isLoading = ref(false)
// eslint-disable-next-line no-unused-vars
const route = useRoute()
const router = useRouter()
const ability = useAbility()

const errors = ref({
  email: undefined,
  password: undefined,
})

const refVForm = ref()

const credentials = ref({
  email: 'admin@salubris.au',
  password: 'admin',
})

const rememberMe = ref(false)

const login = async () => {
  if (isLoading.value) return

  isLoading.value = true
  errors.value = { email: undefined, password: undefined }

  try {
    console.log('Attempting login with:', credentials.value.email)

    // Direct fetch to work with MSW
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: credentials.value.email,
        password: credentials.value.password,
      }),
    })

    console.log('Login response status:', response.status)

    if (!response.ok) {
      const errorData = await response.json()
      console.log('Login error response:', errorData)

      if (errorData.errors) {
        errors.value = errorData.errors
      } else {
        errors.value = {
          email: ['Login failed. Please try again.'],
        }
      }
      return
    }

    const data = await response.json()
    console.log('Login success:', data)

    const { accessToken, userData, userAbilityRules } = data

    // Store auth data in cookies
    useCookie('userAbilityRules').value = userAbilityRules
    useCookie('userData').value = userData
    useCookie('accessToken').value = accessToken

    // Update user abilities
    ability.update(userAbilityRules)

    console.log('Auth data stored, redirecting...')

    await nextTick(() => {
      // Force redirect to dashboards/home specifically
      console.log('Redirecting to dashboard home...')
      router.replace('/dashboards/home')
    })
  } catch (err) {
    console.error('Login error:', err)
    errors.value = {
      email: ['Network error. Please check your connection.'],
    }
  } finally {
    isLoading.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login()
  })
}

// Auto-fill based on selected user type
const selectUserType = (userType) => {
  if (userType === 'admin') {
    credentials.value = {
      email: 'admin@salubris.au',
      password: 'admin',
    }
  } else if (userType === 'client') {
    credentials.value = {
      email: 'client@salubris.au',
      password: 'client',
    }
  }
  errors.value = { email: undefined, password: undefined }
}
</script>

<template>
  <!-- Salubris Australia Header -->
  <RouterLink to="/" class="salubris-auth-header">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <!-- <div class="brand-identity">
        <h1 class="auth-title">
          {{ themeConfig.app.title }}
        </h1> -->
      <!-- <span class="brand-tagline">Nature, enhancing value</span> -->
      <!-- </div> -->
    </div>
  </RouterLink>

  <VRow no-gutters class="auth-wrapper salubris-auth-wrapper">
    <VCol
      cols="12"
      md="12"
      class="auth-card-v2 d-flex align-center justify-center salubris-auth-main"
      style="
        background: linear-gradient(
          135deg,
          rgb(var(--v-theme-surface)) 0%,
          rgb(var(--v-theme-background)) 100%
        );
      "
    >
      <VCard
        flat
        :max-width="520"
        class="mt-12 mt-sm-0 pa-6 salubris-login-card"
        elevation="0"
        style="
          background: rgb(var(--v-theme-surface));
          border: 1px solid rgb(var(--v-theme-outline-variant));
        "
      >
        <!-- Welcome Header -->
        <VCardText class="text-center pb-2">
          <div class="salubris-welcome-icon mb-4">
            <!-- Eucalyptus leaf icon -->
            <!-- <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              class="text-primary"
            >
              <path
                fill="currentColor"
                d="M17,8C8,10 5.9,16.17 3.82,21.34L5.17,22L6.58,17.58C8.13,20.81 10.24,23.06 12.34,22C14.44,20.94 15.79,16.5 16.04,12.66C16.04,12.44 16.04,12.22 16.04,12C16.04,10.89 15.94,9.75 15.71,8.68C16.84,8.52 17.85,8.28 18.71,8C18.71,8 17.71,8.01 17,8M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5Z"
              />
            </svg> -->
          </div>
          <h3 class="text-h4 mb-2 font-weight-medium">
            Welcome to
            <span class="text-primary font-weight-bold">{{
              themeConfig.app.title
            }}</span>
          </h3>
          <p class="text-body-1 text-medium-emphasis mb-6">
            Unified view of tasks, weather data, and asset tracking for
            streamlined monitoring.
          </p>
        </VCardText>

        <!-- Demo Credentials Alert -->
        <VCardText class="pb-2">
          <VAlert
            color="primary"
            variant="tonal"
            class="mb-4"
            border="start"
            border-color="primary"
          >
            <!-- <template #prepend>
              <VIcon icon="ri-information-line" />
            </template> -->
            <div class="text-caption">
              <p class="mb-2 font-weight-medium text-primary">Administrator</p>
              <div class="d-flex align-center gap-2 mb-2">
                <div>
                  Email: <span class="font-weight-bold">admin@salubris.au</span>
                  <br />
                  Password: <span class="font-weight-bold">admin</span>
                </div>
                <VBtn
                  size="x-small"
                  variant="outlined"
                  color="primary"
                  @click="selectUserType('admin')"
                >
                  Use
                </VBtn>
              </div>

              <VDivider class="my-3 opacity-50" />

              <p class="mb-2 font-weight-medium text-primary">User</p>
              <div class="d-flex align-center gap-2 mb-0">
                <div>
                  Email:
                  <span class="font-weight-bold">client@salubris.au</span>
                  <br />
                  Password: <span class="font-weight-bold">client</span>
                </div>
                <VBtn
                  size="x-small"
                  variant="outlined"
                  color="primary"
                  @click="selectUserType('client')"
                >
                  Use
                </VBtn>
              </div>
            </div>
          </VAlert>
        </VCardText>

        <!-- Login Form -->
        <VCardText>
          <VForm ref="refVForm" @submit.prevent="onSubmit">
            <VRow>
              <!-- Email Input -->
              <VCol cols="12">
                <VTextField
                  v-model="credentials.email"
                  label="Email Address"
                  placeholder="your.email@salubris.au"
                  type="email"
                  autofocus
                  variant="outlined"
                  color="primary"
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors.email"
                  :disabled="isLoading"
                  prepend-inner-icon="ri-mail-line"
                  class="salubris-text-field"
                />
              </VCol>

              <!-- Password Input -->
              <VCol cols="12">
                <VTextField
                  v-model="credentials.password"
                  label="Password"
                  placeholder="Enter your password"
                  variant="outlined"
                  color="primary"
                  :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :error-messages="errors.password"
                  :disabled="isLoading"
                  prepend-inner-icon="ri-lock-line"
                  :append-inner-icon="
                    isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'
                  "
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  class="salubris-text-field"
                />

                <!-- Remember Me & Forgot Password -->
                <div
                  class="d-flex align-center flex-wrap justify-space-between my-6 gap-2"
                >
                  <VCheckbox
                    v-model="rememberMe"
                    label="Keep me signed in"
                    color="primary"
                    density="compact"
                    :disabled="isLoading"
                  />
                  <RouterLink
                    class="text-primary text-decoration-none font-weight-medium"
                    :to="{ name: 'forgot-password' }"
                  >
                    Forgot Password?
                  </RouterLink>
                </div>

                <!-- Login Button -->
                <VBtn
                  block
                  type="submit"
                  color="primary"
                  size="large"
                  :loading="isLoading"
                  :disabled="isLoading"
                  class="mb-4 salubris-login-btn"
                  style="
                    text-transform: none;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                  "
                >
                  <VIcon start icon="ri-login-box-line" />
                  {{ isLoading ? 'Authenticating...' : 'Access Dashboard' }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <!-- Footer Info -->
        <VCardText class="text-center pt-0">
          <div class="salubris-auth-footer">
            <p class="text-caption text-medium-emphasis mb-2">
              Deliver sustainable environmental outcomes.
            </p>
            <div class="d-flex justify-center gap-4 text-caption">
              <a href="#" class="text-decoration-none text-medium-emphasis"
                >Privacy Policy</a
              >
              <a href="#" class="text-decoration-none text-medium-emphasis"
                >Terms of Service</a
              >
              <a href="#" class="text-decoration-none text-medium-emphasis"
                >Support</a
              >
            </div>
          </div>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use '@core/scss/template/pages/page-auth.scss';

// Salubris Australia Authentication Styles
.salubris-auth-header {
  text-decoration: none;
  color: inherit;

  .brand-identity {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .auth-title {
      margin-bottom: 0;
      line-height: 1.2;
    }

    .brand-tagline {
      font-size: 0.875rem;
      color: rgb(var(--v-theme-primary));
      font-style: italic;
      margin-top: 2px;
      opacity: 0.8;
    }
  }
}

.salubris-auth-wrapper {
  min-height: 100vh;
}

.salubris-auth-main {
  padding: 2rem 1rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
}

.salubris-login-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(107, 142, 90, 0.08);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    box-shadow: 0 8px 40px rgba(107, 142, 90, 0.12);
  }
}

.salubris-welcome-icon {
  svg {
    filter: drop-shadow(0 2px 4px rgba(107, 142, 90, 0.2));
  }
}

.salubris-text-field {
  .v-field {
    border-radius: 12px;
  }

  .v-field--focused {
    box-shadow: 0 0 0 2px rgba(107, 142, 90, 0.1);
  }
}

.salubris-login-btn {
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-primary)) 0%,
    rgba(var(--v-theme-primary-darken-1)) 100%
  );
  box-shadow: 0 4px 12px rgba(107, 142, 90, 0.3);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(107, 142, 90, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
  }
}

.salubris-illustration-side {
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary), 0.05) 0%,
    rgba(var(--v-theme-background)) 100%
  );

  .salubris-env-content {
    max-width: 600px;
    padding: 2rem;
  }
}

.salubris-auth-footer {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(var(--v-theme-outline-variant), 0.5);

  a {
    transition: color 0.2s ease;

    &:hover {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}

// Dark theme specific adjustments
.v-theme--dark {
  .salubris-login-card {
    background: rgba(var(--v-theme-surface), 0.95) !important;
    backdrop-filter: blur(10px);
  }

  .salubris-auth-main {
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-surface), 0.8) 0%,
      rgba(var(--v-theme-background), 0.9) 100%
    ) !important;
  }
}

// Accessibility improvements
.salubris-text-field input:focus,
.salubris-login-btn:focus {
  outline: 2px solid rgba(var(--v-theme-primary), 0.5);
  outline-offset: 2px;
}

// Responsive adjustments
@media (max-width: 600px) {
  .salubris-auth-header {
    padding: 1rem;

    .auth-logo {
      flex-direction: column;
      text-align: center;
      gap: 0.5rem !important;
    }

    .brand-identity {
      align-items: center;
    }
  }

  .salubris-login-card {
    margin: 1rem;
    padding: 1rem !important;
    max-width: calc(100vw - 2rem) !important;
  }
}
</style>
